import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, CheckSquare, LayoutGrid, List, Clock, Circle, ArrowRight, CheckCircle2, XCircle, Pin } from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';
import { useAuth } from '../../../context/AuthContext';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../../utils/api';
import CreateTaskModal from '../../tasks/CreateTaskModal';
import TaskDetailModal from '../../tasks/TaskDetailModal';
import toast from 'react-hot-toast';

const STATUS_CONFIG = {
  pending: { icon: Circle, color: 'text-gray-400', bg: 'bg-gray-100 dark:bg-gray-700', label: 'To Do', kanbanBg: 'bg-gray-50 dark:bg-gray-800/50' },
  in_progress: { icon: ArrowRight, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/30', label: 'In Progress', kanbanBg: 'bg-blue-50 dark:bg-blue-950/30' },
  completed: { icon: CheckCircle2, color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/30', label: 'Done', kanbanBg: 'bg-green-50 dark:bg-green-950/30' },
  cancelled: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-100 dark:bg-red-900/30', label: 'Cancelled', kanbanBg: 'bg-red-50 dark:bg-red-950/30' },
};

const PRIORITY_CONFIG = {
  urgent: { color: 'bg-red-500', label: 'Urgent', dot: 'bg-red-500' },
  high: { color: 'bg-orange-500', label: 'High', dot: 'bg-orange-500' },
  medium: { color: 'bg-orange-500', label: 'Medium', dot: 'bg-orange-400' },
  low: { color: 'bg-green-500', label: 'Low', dot: 'bg-green-400' },
};

function formatDue(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const days = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
  if (days < 0) return { text: `${Math.abs(days)}d overdue`, cls: 'text-red-500' };
  if (days === 0) return { text: 'Today', cls: 'text-orange-500' };
  if (days === 1) return { text: 'Tomorrow', cls: 'text-orange-500' };
  if (days <= 7) return { text: `${days}d`, cls: 'text-gray-500' };
  return { text: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), cls: 'text-gray-400' };
}

// Kanban card — draggable
function KanbanCard({ task, onSelect, onStatusChange, onTogglePin, theme }) {
  const isDark = theme === 'dark';
  const priority = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
  const due = formatDue(task.due_date);
  const [dragging, setDragging] = useState(false);

  const handleDragStart = (e) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ id: task.id, status: task.status }));
    e.dataTransfer.effectAllowed = 'move';
    setDragging(true);
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={() => setDragging(false)}
      onClick={() => onSelect(task)}
      className={`group p-3 rounded-lg border cursor-grab active:cursor-grabbing transition-all hover:shadow-md ${
        dragging ? 'opacity-40 scale-95' : ''
      } ${
        isDark ? 'bg-gray-800 border-gray-700 hover:border-gray-600' : 'bg-white border-gray-200 hover:border-gray-300'
      } ${task.overdue ? 'border-l-2 border-l-red-500' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-1.5 flex-1 min-w-0">
          {task.pinned && <Pin size={10} className="text-orange-500 fill-orange-500 flex-shrink-0 mt-1" />}
          <h4 className={`text-sm font-rasa font-medium leading-snug ${task.status === 'completed' ? 'line-through opacity-50' : ''}`}>
            {task.title}
          </h4>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onTogglePin(task.id); }}
          className={`p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ${task.pinned ? 'text-orange-500 opacity-100' : 'text-gray-400 hover:text-orange-500'}`}
          title={task.pinned ? 'Unpin' : 'Pin to top'}
        >
          <Pin size={11} />
        </button>
      </div>
      {task.description && (
        <p className={`text-xs font-rasa mt-1 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {task.description.substring(0, 80)}
        </p>
      )}
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-2">
          {task.category && (
            <span className={`text-[9px] font-rasa px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
              {task.category}
            </span>
          )}
          {task.assigned_to && (
            <div className="flex items-center gap-1" title={task.assigned_to.first_name || task.assigned_to.username}>
              <div className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center text-white text-[7px] font-medium flex-shrink-0">
                {(task.assigned_to.first_name || task.assigned_to.username)?.charAt(0)?.toUpperCase()}
              </div>
              <span className={`text-[9px] font-rasa truncate max-w-[60px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {task.assigned_to.first_name || task.assigned_to.username}
              </span>
            </div>
          )}
        </div>
        {due && (
          <span className={`text-[10px] font-rasa flex items-center gap-0.5 ${due.cls}`}>
            <Clock size={9} />
            {due.text}
          </span>
        )}
      </div>
    </div>
  );
}

// Kanban column — drop target
function KanbanColumn({ status, tasks, onSelect, onStatusChange, onTogglePin, theme }) {
  const isDark = theme === 'dark';
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  const [dragOver, setDragOver] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (data.id && data.status !== status) {
        onStatusChange(data.id, status);
      }
    } catch { /* invalid drop */ }
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className={`flex-1 min-w-[240px] max-w-[320px] min-h-0 flex flex-col rounded-xl border transition-all ${config.kanbanBg} ${
        dragOver
          ? `ring-2 ring-blue-500 ${isDark ? 'border-blue-500' : 'border-blue-400'}`
          : `${isDark ? 'border-gray-700' : 'border-gray-200'}`
      }`}
    >
      {/* Column header */}
      <div className="flex items-center justify-between px-3 py-2.5">
        <div className="flex items-center gap-2">
          <Icon size={14} className={config.color} />
          <span className="text-xs font-rasa font-semibold">{config.label}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
            {tasks.length}
          </span>
        </div>
      </div>
      {/* Cards */}
      <div className={`flex-1 overflow-y-auto px-2 pb-2 space-y-2 ${dragOver ? 'min-h-[100px]' : ''}`}>
        {tasks.map(task => (
          <KanbanCard key={task.id} task={task} onSelect={onSelect} onStatusChange={onStatusChange} onTogglePin={onTogglePin} theme={theme} />
        ))}
        {tasks.length === 0 && (
          <div className={`text-center py-8 text-xs font-rasa ${dragOver ? 'text-blue-400' : isDark ? 'text-gray-600' : 'text-gray-400'}`}>
            {dragOver ? 'Drop here' : 'No tasks'}
          </div>
        )}
      </div>
    </div>
  );
}

// List row
function TaskRow({ task, onSelect, onStatusChange, onTogglePin, showAssignee, theme }) {
  const isDark = theme === 'dark';
  const status = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending;
  const priority = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
  const StatusIcon = status.icon;
  const due = formatDue(task.due_date);

  const cycleStatus = (e) => {
    e.stopPropagation();
    const cycle = { pending: 'in_progress', in_progress: 'completed', completed: 'pending', cancelled: 'pending' };
    onStatusChange(task.id, cycle[task.status] || 'pending');
  };

  return (
    <div
      onClick={() => onSelect(task)}
      className={`group flex items-center gap-3 px-3 py-2.5 cursor-pointer border-b transition-colors ${
        isDark ? 'border-gray-800 hover:bg-gray-800/50' : 'border-gray-100 hover:bg-gray-50'
      } ${task.overdue ? 'border-l-2 border-l-red-500' : ''}`}
    >
      <button onClick={cycleStatus} className={`flex-shrink-0 ${status.color}`}>
        <StatusIcon size={18} />
      </button>
      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${priority.dot}`} title={priority.label} />
      {task.pinned && <Pin size={11} className="text-orange-500 fill-orange-500 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <span className={`text-sm font-rasa ${task.status === 'completed' ? 'line-through opacity-50' : ''}`}>
          {task.title}
        </span>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onTogglePin(task.id); }}
        className={`p-1 rounded flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ${task.pinned ? 'text-orange-500 opacity-100' : 'text-gray-400 hover:text-orange-500'}`}
        title={task.pinned ? 'Unpin' : 'Pin to top'}
      >
        <Pin size={12} />
      </button>
      {task.category && (
        <span className={`hidden md:inline text-[10px] font-rasa px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
          {task.category}
        </span>
      )}
      {showAssignee && task.assigned_to && (
        <div className={`hidden md:flex items-center gap-1.5 text-xs font-rasa flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center text-white text-[8px] font-medium flex-shrink-0">
            {(task.assigned_to.first_name || task.assigned_to.username)?.charAt(0)?.toUpperCase()}
          </div>
          <span className="truncate max-w-[80px]">
            {task.assigned_to.first_name || task.assigned_to.username}
          </span>
        </div>
      )}
      {due && (
        <span className={`text-[10px] font-rasa flex items-center gap-0.5 flex-shrink-0 ${due.cls}`}>
          <Clock size={10} />
          {due.text}
        </span>
      )}
    </div>
  );
}

export default function TasksPage() {
  const { theme } = useTheme();
  const { currentUser } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [activeTab, setActiveTab] = useState('assigned_to_me');
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' | 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const isDark = theme === 'dark';
  const isManagerOrAbove = ['manager', 'director', 'admin'].includes(currentUser?.role);

  const tabs = [
    { key: 'involving_me', label: 'All (mine)' },
    { key: 'assigned_to_me', label: 'My Tasks' },
    { key: 'my_tasks', label: 'Created' },
    ...(isManagerOrAbove ? [
      { key: 'assigned_by_me', label: 'Delegated' },
      { key: 'team', label: 'Team' },
    ] : [])
  ];

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter) params.append('status', statusFilter);
      if (priorityFilter) params.append('priority', priorityFilter);

      let endpoint;
      switch (activeTab) {
        case 'involving_me': endpoint = '/tasks/involving_me'; break;
        case 'assigned_to_me': endpoint = '/tasks/assigned_to_me'; break;
        case 'assigned_by_me': endpoint = '/tasks/assigned_by_me'; break;
        case 'team': endpoint = '/tasks/team'; break;
        default: endpoint = '/tasks';
      }

      const response = await apiGet(`${endpoint}?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to load tasks');
      const data = await response.json();
      setTasks(data.tasks || []);
    } catch (err) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchQuery, statusFilter, priorityFilter]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleCreateTask = async (taskData) => {
    try {
      const response = await apiPost('/tasks', taskData);
      if (!response.ok) throw new Error('Failed to create task');
      toast.success('Task created');
      setShowCreateModal(false);
      await fetchTasks();
    } catch (err) {
      toast.error(err.message || 'Failed to create task');
    }
  };

  const handleUpdateStatus = async (taskId, status) => {
    try {
      const response = await apiPatch(`/tasks/${taskId}/status`, { status });
      if (!response.ok) throw new Error('Failed to update status');
      const data = await response.json();
      setTasks(prev => prev.map(t => t.id === taskId ? data.task : t));
      if (selectedTask?.id === taskId) setSelectedTask(data.task);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleUpdateTask = async (taskId, taskData) => {
    try {
      const response = await apiPatch(`/tasks/${taskId}`, taskData);
      if (!response.ok) throw new Error('Failed to update task');
      const data = await response.json();
      setTasks(prev => prev.map(t => t.id === taskId ? data.task : t));
      setSelectedTask(data.task);
      toast.success('Task updated');
    } catch (err) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const response = await apiDelete(`/tasks/${taskId}`);
      if (!response.ok) throw new Error('Failed to delete task');
      setTasks(prev => prev.filter(t => t.id !== taskId));
      setSelectedTask(null);
      toast.success('Task deleted');
    } catch (err) {
      toast.error('Failed to delete task');
    }
  };

  const handleTogglePin = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    try {
      const response = await apiPatch(`/tasks/${taskId}`, { pinned: !task.pinned });
      if (!response.ok) throw new Error('Failed to pin');
      const data = await response.json();
      setTasks(prev => prev.map(t => t.id === taskId ? data.task : t));
    } catch (err) {
      toast.error('Failed to update task');
    }
  };

  // Group tasks by status for kanban
  const kanbanColumns = {
    pending: tasks.filter(t => t.status === 'pending'),
    in_progress: tasks.filter(t => t.status === 'in_progress'),
    completed: tasks.filter(t => t.status === 'completed'),
  };

  const showAssignee = activeTab === 'team' || activeTab === 'assigned_by_me';

  return (
    <div className={`flex flex-col h-full overflow-hidden w-full max-w-screen-2xl mx-auto ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <div className={`flex-shrink-0 flex flex-col md:flex-row md:items-center justify-between px-4 md:px-6 py-3 gap-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center gap-3 ml-8 md:ml-0">
          <CheckSquare size={22} />
          <h1 className="text-lg font-dm-serif font-semibold">Tasks</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Search */}
          <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-sm ${isDark ? 'bg-gray-800' : 'bg-white'} border ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
            <Search size={14} className="text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`bg-transparent outline-none text-xs font-rasa w-28 md:w-36 ${isDark ? 'placeholder-gray-500' : 'placeholder-gray-400'}`}
            />
          </div>
          {/* Filters */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`hidden md:block px-2 py-1.5 rounded-lg text-xs font-rasa border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'}`}
          >
            <option value="">All Status</option>
            <option value="pending">To Do</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Done</option>
          </select>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className={`hidden md:block px-2 py-1.5 rounded-lg text-xs font-rasa border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'}`}
          >
            <option value="">All Priority</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          {/* View toggle */}
          <div className={`flex rounded-lg border ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
            <button
              onClick={() => setViewMode('kanban')}
              className={`p-1.5 rounded-l-lg transition-colors ${viewMode === 'kanban' ? 'bg-blue-600 text-white' : isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              title="Board view"
            >
              <LayoutGrid size={14} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-r-lg transition-colors ${viewMode === 'list' ? 'bg-blue-600 text-white' : isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              title="List view"
            >
              <List size={14} />
            </button>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-rasa"
          >
            <Plus size={14} />
            <span className="hidden md:inline">New Task</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className={`flex-shrink-0 flex gap-0.5 px-4 md:px-6 pt-2 border-b overflow-x-auto ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-3 py-2 text-xs font-rasa font-medium rounded-t-lg transition-colors whitespace-nowrap ${
              activeTab === tab.key
                ? `${isDark ? 'bg-gray-800 text-white' : 'bg-white text-blue-600'} border-b-2 border-blue-600`
                : `${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className={`flex-1 min-h-0 ${viewMode === 'kanban' ? 'overflow-hidden' : 'overflow-auto'}`}>
        {loading ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className={`h-16 rounded-lg animate-pulse ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`} />
            ))}
          </div>
        ) : viewMode === 'kanban' ? (
          /* Kanban Board */
          <div className="flex justify-center gap-4 p-4 md:p-6 h-full min-w-0 overflow-x-auto">
            {['pending', 'in_progress', 'completed'].map(status => (
              <KanbanColumn
                key={status}
                status={status}
                tasks={kanbanColumns[status] || []}
                onSelect={setSelectedTask}
                onStatusChange={handleUpdateStatus}
                onTogglePin={handleTogglePin}
                theme={theme}
              />
            ))}
          </div>
        ) : (
          /* List View */
          <div className="px-1 md:px-3 py-2">
            {tasks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <CheckCircle2 size={40} className="mb-3 opacity-20" />
                <p className="text-sm font-rasa text-gray-500">No tasks found</p>
              </div>
            ) : (
              tasks.map(task => (
                <TaskRow
                  key={task.id}
                  task={task}
                  onSelect={setSelectedTask}
                  onStatusChange={handleUpdateStatus}
                  onTogglePin={handleTogglePin}
                  showAssignee={showAssignee}
                  theme={theme}
                />
              ))
            )}
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateTaskModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateTask}
          theme={theme}
        />
      )}

      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onUpdate={handleUpdateTask}
          onUpdateStatus={handleUpdateStatus}
          onDelete={handleDeleteTask}
          theme={theme}
        />
      )}
    </div>
  );
}
